// Score -> MusicXML. Unlike the legacy Song serializer, this writes every
// part, staff, voice and notehead directly from the score model.

import { chordTypeToHarmony, MUSICXML_TRIAD_KINDS_FIELD } from './kind';
import { IREAL_QUALITY_BY_TYPE } from '../chart';
import { chordParenthesesMetadata, MUSICXML_CHORD_PARENTHESES_FIELD } from './chordParentheses';
import { MUSICXML_BAND_CUE_OTHER_PLAY_TYPE } from './bandCue';
import { MUSICXML_PART_PLAYBACK_OTHER_PLAY_TYPE } from './partPlayback';
import type { IRealChordRef } from '../ireal/types';
import {
  addF,
  compareF,
  durationQuarters,
  frac,
  measureQuarters,
  mulF,
  PPQ,
  type Fraction,
} from '../score/duration';
import { normalizeScore } from '../score/normalize';
import { midiOf, spellMidi, transposePitch } from '../score/pitch';
import {
  MUSICXML_FEEL_OTHER_PLAY_TYPE,
  MUSICXML_FORM_TEXT_OTHER_PLAY_TYPE,
  MUSICXML_PART_ROLE_FIELD,
  MUSICXML_PART_PLAY_ON_FIELD,
} from './feel';
import type {
  Clef,
  FormMeasure,
  NoteEvent,
  NoteHead,
  Part,
  Score,
  Transposition,
  VoiceItem,
} from '../score/types';

const esc = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const FIFTHS_MAJOR: Record<string, number> = {
  C: 0,
  G: 1,
  D: 2,
  A: 3,
  E: 4,
  B: 5,
  'F#': 6,
  'C#': 7,
  F: -1,
  Bb: -2,
  Eb: -3,
  Ab: -4,
  Db: -5,
  Gb: -6,
  Cb: -7,
};
const FIFTHS_MINOR: Record<string, number> = {
  A: 0,
  E: 1,
  B: 2,
  'F#': 3,
  'C#': 4,
  'G#': 5,
  'D#': 6,
  'A#': 7,
  D: -1,
  G: -2,
  C: -3,
  F: -4,
  Bb: -5,
  Eb: -6,
  Ab: -7,
};

function units(value: Fraction): number {
  return Math.max(0, Math.round((value.num * PPQ) / value.den));
}

function moveXml(tag: 'backup' | 'forward', value: Fraction, voice?: number): string {
  const duration = units(value);
  if (duration <= 0) return '';
  const voiceXml = voice === undefined ? '' : `<voice>${voice}</voice>`;
  return `<${tag}><duration>${duration}</duration>${voiceXml}</${tag}>`;
}

function pitchXml(head: NoteHead, transposition?: Transposition): string {
  // The model stores CONCERT truth; MusicXML writes a transposing part's
  // WRITTEN view (concert + transposition) with a <transpose> element saying
  // how to get the sound back. Falls back to a MIDI-true respelling when the
  // diatonic result would need a triple accidental (the toConcert pattern).
  let pitch = head.pitch;
  if (transposition) {
    try {
      pitch = transposePitch(pitch, transposition);
    } catch {
      pitch = spellMidi(midiOf(head.pitch) + transposition.semitones);
    }
  }
  const alter = pitch.alter ? `<alter>${pitch.alter}</alter>` : '';
  return `<pitch><step>${pitch.step}</step>${alter}<octave>${pitch.octave}</octave></pitch>`;
}

function tieXml(head: NoteHead): string {
  if (head.tie === 'both') return '<tie type="stop"/><tie type="start"/>';
  return head.tie ? `<tie type="${head.tie}"/>` : '';
}

interface FlatEvent {
  event: NoteEvent;
  factor: Fraction;
  tupletStart: boolean;
  tupletStop: boolean;
}

interface NumberedMark {
  number: number;
  side?: 'above' | 'below';
}

interface NumberedWedge extends NumberedMark {
  type: 'cresc' | 'dim';
}

interface EventSpannerMarks {
  slurStarts: NumberedMark[];
  slurStops: NumberedMark[];
  wedgeStarts: NumberedWedge[];
  wedgeStops: NumberedWedge[];
}

function flattenItems(items: VoiceItem[], factor: Fraction = frac(1)): FlatEvent[] {
  const out: FlatEvent[] = [];
  for (const item of items) {
    if (item.kind !== 'tuplet') {
      out.push({ event: item, factor, tupletStart: false, tupletStop: false });
      continue;
    }
    const nested = flattenItems(item.items, mulF(factor, frac(item.normal, item.actual)));
    if (nested.length > 0) {
      nested[0] = { ...nested[0], tupletStart: true };
      nested[nested.length - 1] = { ...nested[nested.length - 1], tupletStop: true };
      out.push(...nested);
    }
  }
  return out;
}

function partSpannerMarks(score: Score, part: Part): Map<string, EventSpannerMarks> {
  const eventIds = new Set<string>();
  for (const measure of part.measures) {
    for (const voice of measure.voices) {
      for (const { event } of flattenItems(voice.items)) eventIds.add(event.id);
    }
  }

  const marks = new Map<string, EventSpannerMarks>();
  const at = (eventId: string): EventSpannerMarks => {
    let hit = marks.get(eventId);
    if (!hit) {
      hit = { slurStarts: [], slurStops: [], wedgeStarts: [], wedgeStops: [] };
      marks.set(eventId, hit);
    }
    return hit;
  };

  let slurNumber = 0;
  let wedgeNumber = 0;
  for (const spanner of score.spanners ?? []) {
    if (!eventIds.has(spanner.fromEventId) || !eventIds.has(spanner.toEventId)) continue;
    if (spanner.type === 'slur') {
      const mark: NumberedMark = {
        number: ++slurNumber,
        ...(spanner.side ? { side: spanner.side } : {}),
      };
      at(spanner.fromEventId).slurStarts.push(mark);
      at(spanner.toEventId).slurStops.push(mark);
    } else if (spanner.type === 'cresc' || spanner.type === 'dim') {
      const mark: NumberedWedge = {
        number: ++wedgeNumber,
        type: spanner.type,
        ...(spanner.side ? { side: spanner.side } : {}),
      };
      at(spanner.fromEventId).wedgeStarts.push(mark);
      at(spanner.toEventId).wedgeStops.push(mark);
    }
  }
  return marks;
}

const ORNAMENT_XML_TAGS: Record<NonNullable<NoteEvent['ornaments']>[number], string> = {
  trill: 'trill-mark',
  turn: 'turn',
  'inverted-turn': 'inverted-turn',
  mordent: 'mordent',
  'inverted-mordent': 'inverted-mordent',
};

const ARTICULATION_XML: Record<NonNullable<NoteEvent['articulations']>[number], string> = {
  staccato: '<staccato/>',
  accent: '<accent/>',
  tenuto: '<tenuto/>',
  marcato: '<strong-accent type="up"/>',
};

function eventXml(
  flat: FlatEvent,
  voice: number,
  homeStaff: number,
  marks?: EventSpannerMarks,
  transposition?: Transposition,
  clef: Clef = 'treble',
): string {
  const { event, factor } = flat;
  const sounding = event.grace ? frac(0) : mulF(durationQuarters(event.duration), factor);
  const duration = event.grace ? '' : `<duration>${units(sounding)}</duration>`;
  const dots = '<dot/>'.repeat(event.duration.dots ?? 0);
  const staff = (event.staff ?? homeStaff) + 1;
  const timeModification =
    factor.num === factor.den
      ? ''
      : `<time-modification><actual-notes>${factor.den}</actual-notes><normal-notes>${factor.num}</normal-notes></time-modification>`;
  const tupletMarks = `${flat.tupletStart ? '<tuplet type="start"/>' : ''}${
    flat.tupletStop ? '<tuplet type="stop"/>' : ''
  }`;
  // Stops precede starts so chained slurs sharing an endpoint pair correctly
  // in readers that process notations in document order.
  const slurs = `${(marks?.slurStops ?? [])
    .map((mark) => `<slur type="stop" number="${mark.number}"/>`)
    .join('')}${(marks?.slurStarts ?? [])
    .map(
      (mark) =>
        `<slur type="start" number="${mark.number}"${mark.side ? ` placement="${mark.side}"` : ''}/>`,
    )
    .join('')}`;
  const ornaments = event.ornaments?.length
    ? `<ornaments>${event.ornaments
        .map((ornament) => `<${ORNAMENT_XML_TAGS[ornament]}/>`)
        .join('')}</ornaments>`
    : '';
  const articulations = event.articulations?.length
    ? `<articulations>${event.articulations.map((a) => ARTICULATION_XML[a]).join('')}</articulations>`
    : '';
  const dynamic = event.dynamic ? `<dynamics><${event.dynamic}/></dynamics>` : '';
  const notations =
    slurs || tupletMarks || ornaments || articulations || dynamic
      ? `<notations>${slurs}${tupletMarks}${ornaments}${articulations}${dynamic}</notations>`
      : '';
  const lyric = (event.lyrics ?? [])
    .map((syl, verse) =>
      syl
        ? `<lyric number="${verse + 1}"><syllabic>${syl.hyphen ? 'begin' : 'single'}</syllabic><text>${esc(syl.text)}</text></lyric>`
        : '',
    )
    .join('');
  const grace = event.grace ? '<grace/>' : '';
  // Native cue-size notes still sound. MusicXML's <cue/> instead makes a
  // silent note and forbids sound ties, so preserve our semantics with size.
  const type = `<type${event.cue && !event.grace ? ' size="cue"' : ''}>${event.duration.base}</type>`;
  const common = `${duration}<voice>${voice}</voice>${type}${dots}${timeModification}<staff>${staff}</staff>`;

  if (event.slash) {
    const [step, octave] = clef === 'bass' ? ['D', 3]
      : clef === 'alto' ? ['C', 4] : clef === 'tenor' ? ['A', 3] : ['B', 4];
    const stem = event.slash === 'beat' ? 'none' : 'down';
    return `<note>${grace}<unpitched><display-step>${step}</display-step><display-octave>${octave}</display-octave></unpitched>` +
      `${duration}<voice>${voice}</voice>${type}${dots}${timeModification}<stem>${stem}</stem><notehead>slash</notehead>` +
      `<staff>${staff}</staff>${notations}${lyric}</note>`;
  }
  if (event.kind === 'rest' || event.notes.length === 0) {
    return `<note>${grace}<rest/>${common}${notations}${lyric}</note>`;
  }
  return event.notes
    .map(
      (head, index) =>
        `<note>${grace}${index > 0 ? '<chord/>' : ''}${pitchXml(head, transposition)}${duration}${tieXml(head)}` +
        `<voice>${voice}</voice>${type}${dots}${timeModification}<staff>${staff}</staff>` +
        `${index === 0 ? notations + lyric : ''}</note>`,
    )
    .join('');
}

function wedgeDirectionXml(mark: NumberedWedge, phase: 'start' | 'stop', staff: number): string {
  const type = phase === 'stop' ? 'stop' : mark.type === 'cresc' ? 'crescendo' : 'diminuendo';
  const placement = mark.side ? ` placement="${mark.side}"` : '';
  return `<direction${placement}><direction-type><wedge type="${type}" number="${mark.number}"/></direction-type><staff>${staff}</staff></direction>`;
}

function clefXml(clef: Clef, number: number): string {
  const attr = number > 1 ? ` number="${number}"` : '';
  if (clef === 'bass') return `<clef${attr}><sign>F</sign><line>4</line></clef>`;
  if (clef === 'alto') return `<clef${attr}><sign>C</sign><line>3</line></clef>`;
  if (clef === 'tenor') return `<clef${attr}><sign>C</sign><line>4</line></clef>`;
  if (clef === 'percussion') return `<clef${attr}><sign>percussion</sign><line>2</line></clef>`;
  const octave = clef === 'treble8vb' ? '<clef-octave-change>-1</clef-octave-change>' : '';
  return `<clef${attr}><sign>G</sign><line>2</line>${octave}</clef>`;
}

function attributesXml(measure: FormMeasure, part: Part, index: number): string {
  let body = index === 0 ? `<divisions>${PPQ}</divisions>` : '';
  if (measure.keySig) {
    // The form key is CONCERT; a transposing part's staff shows its WRITTEN
    // key — the concert signature moved by 7S − 12D fifths, enharmonically
    // wrapped (tenor's +14 semitones = +2 fifths: concert E reads F#).
    let fifths =
      (measure.keySig.mode === 'minor' ? FIFTHS_MINOR : FIFTHS_MAJOR)[measure.keySig.tonic] ?? 0;
    if (part.transposition) {
      fifths += 7 * part.transposition.semitones - 12 * part.transposition.degrees;
      while (fifths > 7) fifths -= 12;
      while (fifths < -7) fifths += 12;
    }
    body += `<key><fifths>${fifths}</fifths><mode>${measure.keySig.mode}</mode></key>`;
  }
  if (measure.timeSig) {
    body += `<time><beats>${measure.timeSig[0]}</beats><beat-type>${measure.timeSig[1]}</beat-type></time>`;
  }
  if (index === 0) {
    if (part.staves.length > 1) body += `<staves>${part.staves.length}</staves>`;
    body += part.staves.map((staff, i) => clefXml(staff.clef, i + 1)).join('');
    if (part.transposition) {
      // MusicXML: sounding = written + transpose, so the element is the
      // INVERSE of Part.transposition (written = concert + T), with whole
      // octaves folded into octave-change as engravers write it. Writing +T
      // here (the model's own field, verbatim) shipped every re-imported
      // tenor a major NINTH sharp — the bundled George's Dilemma lead, found
      // by ear.
      const semitones = -part.transposition.semitones;
      const degrees = -part.transposition.degrees;
      const octaves = Math.trunc(semitones / 12);
      const chromatic = semitones - 12 * octaves;
      const diatonic = degrees - 7 * octaves;
      body +=
        `<transpose><diatonic>${diatonic}</diatonic><chromatic>${chromatic}</chromatic>` +
        `${octaves ? `<octave-change>${octaves}</octave-change>` : ''}</transpose>`;
    }
  }
  return body ? `<attributes>${body}</attributes>` : '';
}

function rootXml(note: string, tag: 'root' | 'bass'): string {
  const step = note[0]?.toUpperCase() ?? 'C';
  let alter = 0;
  for (const accidental of note.slice(1)) {
    if (accidental === '#' || accidental === '♯') alter++;
    if (accidental === 'b' || accidental === '♭') alter--;
  }
  const alterXml = alter ? `<${tag}-alter>${alter}</${tag}-alter>` : '';
  return `<${tag}><${tag}-step>${step}</${tag}-step>${alterXml}</${tag}>`;
}

function harmonySymbolXml(chord: IRealChordRef, alternate = false): string {
  if (!chord.chordType || !chord.root) return '';
  const { kind, degrees } = chordTypeToHarmony(chord.chordType);
  const degreeXml = degrees
    .map(
      (degree) =>
        `<degree><degree-value>${degree.value}</degree-value><degree-alter>${degree.alter}</degree-alter><degree-type>${degree.type}</degree-type></degree>`,
    )
    .join('');
  const kindText =
    chord.quality !== IREAL_QUALITY_BY_TYPE[chord.chordType] ? ` text="${esc(chord.quality)}"` : '';
  return `<harmony${alternate ? ' type="alternate"' : ''}>${rootXml(chord.root, 'root')}<kind${kindText}>${kind}</kind>${
    chord.bass ? rootXml(chord.bass, 'bass') : ''
  }${degreeXml}</harmony>`;
}

function harmonyXml(measure: FormMeasure, meter: [number, number]): string {
  const quarterPerBeat = frac(4, meter[1]);
  let position = frac(0);
  let cursor = frac(0);
  let xml = '';
  for (const chord of measure.harmony) {
    if (chord.noChord || (chord.chordType && chord.root)) {
      const delta = addF(position, frac(-cursor.num, cursor.den));
      if (compareF(delta, frac(0)) > 0) xml += moveXml('forward', delta);
      cursor = position;
      if (chord.noChord) {
        // MusicXML's canonical no-harmony value is kind="none". A function
        // satisfies harmony's required root/function/numeral choice without
        // inventing a sounding root that another program might play.
        xml += '<harmony><function>N.C.</function><kind text="N.C.">none</kind></harmony>';
      } else {
        xml += harmonySymbolXml(chord);
        if (chord.alternate) xml += harmonySymbolXml(chord.alternate, true);
      }
    }
    position = addF(position, mulF(frac(Math.max(0, chord.beats || 0)), quarterPerBeat));
  }
  if (compareF(cursor, frac(0)) > 0) xml += moveXml('backup', cursor);
  return xml;
}

/** MusicXML can preserve a typed Feel as a visible directive and a standard
 * `other-play` marker that this app can recover on import. */
function feelDirectionXml(measure: FormMeasure): string {
  const label = measure.feel?.label.trim();
  if (!label) return '';
  const offset = measure.feel?.offset ? units(measure.feel.offset) : 0;
  const offsetXml = offset > 0 ? `<offset>${offset}</offset>` : '';
  return (
    `<direction placement="above" directive="yes">` +
    `<direction-type><words>${esc(label)}</words></direction-type>${offsetXml}` +
    `<sound><play><other-play type="${MUSICXML_FEEL_OTHER_PLAY_TYPE}">${esc(label)}</other-play></play></sound>` +
    `</direction>`
  );
}

interface EndingClose {
  number: number;
  type: 'stop' | 'discontinue';
}

function endingCloses(form: FormMeasure[]): Map<number, EndingClose> {
  const closes = new Map<number, EndingClose>();
  let active: number | undefined;
  form.forEach((measure, index) => {
    if (measure.ending !== undefined) active = measure.ending;
    const nextStarts = form[index + 1]?.ending !== undefined;
    const boundary =
      measure.close === 'repeat' ||
      measure.close === 'final' ||
      nextStarts ||
      index === form.length - 1;
    if (active !== undefined && boundary) {
      closes.set(index, {
        number: active,
        type: measure.close === 'repeat' ? 'stop' : 'discontinue',
      });
      active = undefined;
    }
  });
  return closes;
}

function leftBarlineXml(measure: FormMeasure): string {
  const style =
    measure.open === 'repeat'
      ? '<bar-style>heavy-light</bar-style>'
      : measure.open === 'double'
        ? '<bar-style>light-light</bar-style>'
        : '';
  const ending =
    measure.ending !== undefined ? `<ending number="${measure.ending}" type="start"/>` : '';
  const repeat = measure.open === 'repeat' ? '<repeat direction="forward"/>' : '';
  const body = `${style}${ending}${repeat}`;
  return body ? `<barline location="left">${body}</barline>` : '';
}

function rightBarlineXml(measure: FormMeasure, endingClose?: EndingClose): string {
  const style =
    measure.close === 'repeat' || measure.close === 'final'
      ? '<bar-style>light-heavy</bar-style>'
      : measure.close === 'double'
        ? '<bar-style>light-light</bar-style>'
        : '';
  const ending = endingClose
    ? `<ending number="${endingClose.number}" type="${endingClose.type}"/>`
    : '';
  const repeatTimes = /^\s*(\d+)x\s*$/i.exec(measure.staffText ?? '')?.[1];
  const repeat =
    measure.close === 'repeat'
      ? `<repeat direction="backward"${repeatTimes ? ` times="${repeatTimes}"` : ''}/>`
      : '';
  // Fermata is measure-level in the score model, so use MusicXML's barline
  // fermata rather than inventing a note-level attachment.
  const fermata = measure.fermata ? '<fermata type="upright"/>' : '';
  const body = `${style}${fermata}${ending}${repeat}`;
  return body ? `<barline location="right">${body}</barline>` : '';
}

function emptyPart(score: Score): Part {
  return {
    id: 'part:lead',
    name: 'Lead',
    staves: [{ id: 'staff:lead:0', clef: 'treble', staffType: 'standard' }],
    measures: score.form.map((measure) => ({ id: `pm:${measure.id}`, voices: [] })),
  };
}

function partXml(score: Score, part: Part, partIndex: number): string {
  let meter: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  const spannerMarks = partSpannerMarks(score, part);
  const endingCloseByMeasure = endingCloses(score.form);
  const measures = score.form.map((formMeasure, measureIndex) => {
    meter = formMeasure.timeSig ?? meter;
    const capacity = formMeasure.pickupBeats
      ? mulF(frac(Math.round(formMeasure.pickupBeats * PPQ), PPQ), frac(4, meter[1]))
      : measureQuarters(meter);
    const partMeasure = part.measures[measureIndex];
    let body = attributesXml(formMeasure, part, measureIndex);
    body += leftBarlineXml(formMeasure);
    if (partMeasure?.playback?.length) {
      body += `<direction><direction-type><other-direction print-object="no">Part playback</other-direction></direction-type><sound><play><other-play type="${MUSICXML_PART_PLAYBACK_OTHER_PLAY_TYPE}">${esc(JSON.stringify(partMeasure.playback))}</other-play></play></sound></direction>`;
    }
    if (partIndex === 0) {
      // Sections round-trip as rehearsal marks (the importer also reads the
      // Sibelius boxed-words spelling, but <rehearsal> is the canonical one).
      if (formMeasure.section) {
        body += `<direction placement="above"><direction-type><rehearsal>${esc(formMeasure.section)}</rehearsal></direction-type></direction>`;
      }
      // Form navigation signs apply before the marked measure is played.
      for (const sign of ['segno', 'coda'] as const) {
        if (formMeasure[sign]) {
          body += `<direction placement="above"><direction-type><${sign}/></direction-type></direction>`;
        }
      }
      body += harmonyXml(formMeasure, meter);
      body += feelDirectionXml(formMeasure);
      if (formMeasure.bandCue) {
        // other-direction supplies the required direction-type, invisibly;
        // staffText below remains the single source of printed cue wording.
        body += `<direction><direction-type><other-direction print-object="no">Band cue</other-direction></direction-type><sound><play><other-play type="${MUSICXML_BAND_CUE_OTHER_PLAY_TYPE}">${esc(JSON.stringify(formMeasure.bandCue))}</other-play></play></sound></direction>`;
      }
      if (formMeasure.staffText) {
        const words = esc(formMeasure.staffText);
        const placement = formMeasure.staffTextAbove ? 'above' : 'below';
        body += `<direction placement="${placement}"><direction-type><words>${words}</words></direction-type><sound><play><other-play type="${MUSICXML_FORM_TEXT_OTHER_PLAY_TYPE}">${words}</other-play></play></sound></direction>`;
      }
    }
    if (measureIndex === 0 && (score.tempoText || score.tempo)) {
      const words = score.tempoText
        ? `<direction-type><words>${esc(score.tempoText)}</words></direction-type>`
        : '';
      const beatUnit = ({ 1: 'whole', 2: 'half', 4: 'quarter', 8: 'eighth', 16: '16th', 32: '32nd', 64: '64th', 128: '128th' } as Record<number, string>)[meter[1]] ?? 'quarter';
      const metronome = score.tempo
        ? `<direction-type><metronome><beat-unit>${beatUnit}</beat-unit><per-minute>${Math.round(score.tempo)}</per-minute></metronome></direction-type>`
        : '';
      const sound = score.tempo ? `<sound tempo="${score.tempo * 4 / meter[1]}"/>` : '';
      const directive = score.tempoText ? ' directive="yes"' : '';
      body += `<direction placement="above"${directive}>${words}${metronome}${sound}</direction>`;
    }

    const voices = partMeasure?.voices ?? [];
    if (voices.length === 0) {
      body += `<note><rest measure="yes"/><duration>${units(capacity)}</duration><voice>1</voice><staff>1</staff></note>`;
    } else {
      let previousLength = frac(0);
      voices.forEach((voice, voiceIndex) => {
        if (voiceIndex > 0) body += moveXml('backup', previousLength);
        const flat = flattenItems(voice.items);
        body += flat
          .map((event) => {
            // Staff text travels as a words direction placed before its note.
            const wordsDir = (side: 'above' | 'below', t?: string) =>
              t
                ? `<direction placement="${side}"><direction-type><words>${esc(t)}</words></direction-type><staff>${(voice.staff ?? 0) + 1}</staff></direction>`
                : '';
            const words =
              wordsDir('above', event.event.words) + wordsDir('below', event.event.wordsBelow);
            const marks = spannerMarks.get(event.event.id);
            const staff = (event.event.staff ?? voice.staff ?? 0) + 1;
            const wedgeStarts = (marks?.wedgeStarts ?? [])
              .map((mark) => wedgeDirectionXml(mark, 'start', staff))
              .join('');
            const wedgeStops = (marks?.wedgeStops ?? [])
              .map((mark) => wedgeDirectionXml(mark, 'stop', staff))
              .join('');
            return (
              wedgeStarts +
              words +
              eventXml(event, voiceIndex + 1, voice.staff ?? 0, marks, part.transposition, part.staves[event.event.staff ?? voice.staff ?? 0]?.clef) +
              wedgeStops
            );
          })
          .join('');
        let length = flat.reduce(
          (sum, item) =>
            item.event.grace
              ? sum
              : addF(sum, mulF(durationQuarters(item.event.duration), item.factor)),
          frac(0),
        );
        if (compareF(length, capacity) < 0) {
          const remainder = addF(capacity, frac(-length.num, length.den));
          body += moveXml('forward', remainder, voiceIndex + 1);
          length = capacity;
        }
        previousLength = length;
      });
    }
    // D.S./D.C./Fine commands take effect after the written bar. Placing
    // their words here also keeps them separate from annotations on notes.
    if (partIndex === 0 && formMeasure.directive) {
      body += `<direction placement="above"><direction-type><words>${esc(formMeasure.directive)}</words></direction-type></direction>`;
    }
    body += rightBarlineXml(formMeasure, endingCloseByMeasure.get(measureIndex));
    const implicit = formMeasure.pickupBeats ? ' implicit="yes"' : '';
    return `<measure number="${measureIndex + 1}"${implicit}>${body}</measure>`;
  });
  return `<part id="P${partIndex + 1}">${measures.join('')}</part>`;
}

function encodeMusicXMLScore(input: Score): string {
  const score = normalizeScore(input);
  const parts = score.parts.length > 0 ? score.parts : [emptyPart(score)];
  const partList = parts
    .map(
      (part, index) =>
        // MusicXML has no part-role vocabulary; a miscellaneous-field in the
        // score-part's identification carries ours through a round trip (the
        // DTD puts identification BEFORE part-name).
        `<score-part id="P${index + 1}">${
          part.role || part.playOn
            ? `<identification><miscellaneous>${
                part.role
                  ? `<miscellaneous-field name="${MUSICXML_PART_ROLE_FIELD}">${part.role}</miscellaneous-field>`
                  : ''
              }${
                part.playOn
                  ? `<miscellaneous-field name="${MUSICXML_PART_PLAY_ON_FIELD}">${part.playOn}</miscellaneous-field>`
                  : ''
              }</miscellaneous></identification>`
            : ''
        }<part-name>${esc(part.name)}</part-name>${
          part.shortName ? `<part-abbreviation>${esc(part.shortName)}</part-abbreviation>` : ''
        }</score-part>`,
    )
    .join('');
  // Keep standard chord kinds for other readers while distinguishing authored
  // triads from the four-part interpretation used for historical imports.
  const parentheses = chordParenthesesMetadata(score);
  const identification = `<identification>${
    score.composer ? `<creator type="composer">${esc(score.composer)}</creator>` : ''
  }<miscellaneous><miscellaneous-field name="${MUSICXML_TRIAD_KINDS_FIELD}">literal</miscellaneous-field>${
    parentheses ? `<miscellaneous-field name="${MUSICXML_CHORD_PARENTHESES_FIELD}">${esc(parentheses)}</miscellaneous-field>` : ''
  }</miscellaneous></identification>`;
  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">` +
    `<score-partwise version="4.0"><work><work-title>${esc(score.title)}</work-title></work>${identification}` +
    `<part-list>${partList}</part-list>${parts.map((part, index) => partXml(score, part, index)).join('')}` +
    `</score-partwise>`
  );
}

export interface MusicXMLExportResult {
  xml: string;
  /** Reserved for future format degradations; Feel cues are fully represented. */
  warnings: string[];
}

/** Serialize MusicXML and return the same payload-plus-report shape as iReal. */
export function exportMusicXMLScore(input: Score): MusicXMLExportResult {
  return { xml: encodeMusicXMLScore(input), warnings: [] };
}

/** Compatibility string helper for pure round-trip callers. */
export function serializeMusicXMLScore(input: Score): string {
  return exportMusicXMLScore(input).xml;
}
